import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

export type HogcloudChatPose = 'neutral' | 'focused' | 'thinking' | 'helpful' | 'surprised' | 'celebrate';

export type HogcloudChatReply = {
  content: ReactNode;
  pose: HogcloudChatPose;
};

type HogcloudChatRule = {
  keywords: string[];
  reply: HogcloudChatReply;
};

export const hogcloudChatIntro: HogcloudChatReply = {
  pose: 'neutral',
  content: (
    <>
      Hi, I&apos;m ArkieByte. Ask me about Hogcloud websites, apps, 3D prints, tech
      consulting, or how to get a project started.
    </>
  ),
};

export const hogcloudChatQuickPrompts = [
  { label: 'Services', message: 'What services does Hogcloud offer?' },
  { label: 'Websites', message: 'Can you build a website?' },
  { label: 'Pricing', message: 'What are Hogcloud pricing rates?' },
  { label: 'Email', message: 'Can you set up a business email domain?' },
  { label: 'Chatbots', message: 'Do you make chatbots?' },
];

const chatRules: HogcloudChatRule[] = [
  {
    keywords: ['email', 'e-mail', 'mailbox', 'mailboxes', 'domain email', 'email domain', 'gmail', 'hotmail', 'spam', 'junk', 'dns', 'spf', 'dkim', 'dmarc'],
    reply: {
      pose: 'helpful',
      content: (
        <>
          Yes. Hogcloud can set up a professional email domain, configure DNS verification,
          help with forwarding from existing mailboxes, and document how to use the new boxes.
          The one-time setup is $200, with domain registration typically $10-$15/year and
          mailbox hosting around $1/month. See the{' '}
          <Link to="/services/websites">websites service page</Link>.
        </>
      ),
    },
  },
  {
    keywords: ['website', 'web site', 'site', 'landing page', 'online'],
    reply: {
      pose: 'helpful',
      content: (
        <>
          Hogcloud builds practical small-business websites with clear service pages,
          contact flows, SEO basics, and optional add-ons like scheduling, SMS, customer
          logins, and chatbots. You can explore details on the{' '}
          <Link to="/services/websites">websites service page</Link>.
        </>
      ),
    },
  },
  {
    keywords: ['chatbot', 'bot', 'ai', 'faq', 'assistant'],
    reply: {
      pose: 'celebrate',
      content: (
        <>
          Yes. Hogcloud can add friendly FAQ chatbots and more integrated AI chatbots for
          websites. The deeper service overview is on the{' '}
          <Link to="/services/websites/intelligent-chatbots">intelligent chatbots page</Link>.
        </>
      ),
    },
  },
  {
    keywords: ['app', 'application', 'software', 'tool', 'dashboard'],
    reply: {
      pose: 'thinking',
      content: (
        <>
          Hogcloud can help with custom app development, internal tools, dashboards, and
          workflow software. Start with the{' '}
          <Link to="/services/appdev">app development page</Link> for the broad strokes.
        </>
      ),
    },
  },
  {
    keywords: ['3d', 'print', 'printing', 'plastic', 'prototype', 'model'],
    reply: {
      pose: 'helpful',
      content: (
        <>
          For physical parts, prototypes, gifts, and custom objects, check out{' '}
          <Link to="/services/3dprints">custom 3D prints</Link>. Hogcloud can help turn a
          small idea into something you can hold.
        </>
      ),
    },
  },
  {
    keywords: ['consult', 'consulting', 'help', 'computer', 'tech', 'technology', 'support'],
    reply: {
      pose: 'thinking',
      content: (
        <>
          Tech consulting is a good fit when you need help choosing tools, untangling a
          workflow, or planning a practical next step. The{' '}
          <Link to="/services/consulting">consulting page</Link> has more context.
        </>
      ),
    },
  },
  {
    keywords: ['rate', 'rates', 'hourly', 'retainer', 'retainers', 'price', 'pricing', 'cost', 'costs'],
    reply: {
      pose: 'helpful',
      content: (
        <>
          Hogcloud rates include cloud support at $125/hr, software development at
          $150/hr, cloud architecture at $175/hr, AI consulting at $175-$200/hr,
          emergency or after-hours support at $225/hr, electronics at $150/hr, and
          tutoring at $50/hr. See the <Link to="/pricing">pricing page</Link>.
        </>
      ),
    },
  },
  {
    keywords: ['start', 'quote', 'estimate', 'contact', 'hire'],
    reply: {
      pose: 'helpful',
      content: (
        <>
          The easiest next step is to send a note with what you&apos;re trying to build,
          your timeline, and any examples you like. Head to the{' '}
          <Link to="/contact">contact page</Link> and Hogcloud can follow up from there.
        </>
      ),
    },
  },
  {
    keywords: ['service', 'offer', 'offering', 'do you do', 'what do you do'],
    reply: {
      pose: 'celebrate',
      content: (
        <>
          Hogcloud works across custom 3D prints, websites, app development, and tech
          consulting. Pick a service from the home page, or browse the{' '}
          <Link to="/services">services page</Link>.
        </>
      ),
    },
  },
  {
    keywords: ['portfolio', 'work', 'examples', 'sample'],
    reply: {
      pose: 'helpful',
      content: (
        <>
          You can see recent website and project examples in the{' '}
          <Link to="/portfolio">portfolio</Link>.
        </>
      ),
    },
  },
];

function messageMatchesKeyword(message: string, keyword: string, tokens: string[]): boolean {
  if (keyword.includes(' ')) {
    return message.includes(keyword);
  }

  return tokens.some((token) => token === keyword || token === `${keyword}s`);
}

export function getHogcloudChatReply(message: string): HogcloudChatReply {
  const normalizedMessage = message.toLowerCase();
  const messageTokens = normalizedMessage.match(/[a-z0-9]+/g) || [];
  const matchedRule = chatRules.find((rule) =>
    rule.keywords.some((keyword) =>
      messageMatchesKeyword(normalizedMessage, keyword, messageTokens),
    ),
  );

  if (matchedRule) {
    return matchedRule.reply;
  }

  return {
    pose: 'surprised',
    content: (
      <>
        I may not have that answer yet. Try asking about websites, chatbots, apps, 3D
        prints, consulting, pricing, or how to start a project. You can also go straight
        to <Link to="/contact">contact</Link>.
      </>
    ),
  };
}
